import { makeAutoObservable } from 'mobx'
import { UploadFile } from 'antd'
import { vacancyApi } from '@/api'
import { vacancyDictionary } from '@/dictionary/uz/vacancy'
import { addCatchNotification, addSuccessNotification } from '@/modules/notifications'
import {
  Branch,
  EditNewsType,
  EditVacancyType,
  FaqEditType,
  FaqsRes,
  FaqType,
  ICandidateParams,
  ICandidateRes,
  INewAddValues,
  IPagination,
  IVacanciesParams,
  IVacanciesRes,
  IVacancyType,
  NewsCategoryType,
  NewsParams,
  NewsResponse,
  NewsType,
  OurTeamImage,
  Position,
  PostType,
  Statuses,
  VacancyPositionChange,
} from '@/types'
import { makeFileUrl } from '@/utils'
import { NewsModal } from './types'

class VacancyStore {
  vacanciesModal = false
  vacanciesActiveTab: Statuses = Statuses.Active
  isPreviewImgVacancies = false
  previewImgUrl: string | null = null
  FAQModal = false
  newsModal: NewsModal | null = null
  topNew: NewsType | null = null
  recommendNew: NewsType | null = null
  isPreviewNewsImg = false
  previewNewsImgUrl: string | null = null
  vacancies: IVacanciesRes | null = null
  vacanciesLoading = false
  vacanciesFilter: IVacanciesParams = {
    page: 1,
    perPage: 10,
  }

  vacanciesEditData: IVacancyType | null = null
  vacancyTitle: string | null = null
  candidates: ICandidateRes | null = null
  candidatesLoading = false
  branches: Branch[] = []
  candidatesFilter: ICandidateParams = {
    page: 1,
    perPage: 10,
  }

  faqs: FaqsRes | null = null
  faqEditData: FaqType | null = null
  faqsLoading = false
  faqsFilter: IPagination = {
    page: 1,
    perPage: 10,
  }

  ourTeamImages: UploadFile[] = []
  ourTeamImagesLoading = false
  newsCategoryModal = false
  allNews: NewsResponse | null = null
  newsCategoryName: string | null = null
  newsLoading = false
  categoriesEditData: NewsCategoryType | null = null
  recommendNewsLoading = false
  topNewsLoading = false
  newsActiveTab: Statuses = Statuses.Active
  newsSingle: NewsType | null = null
  newsFilter: NewsParams = {
    page: 1,
    perPage: 10,
  }

  constructor() {
    makeAutoObservable(this)
  }

  setNewsSingle = (params: NewsType | null) => {
    this.newsSingle = params
  }

  setCategoriesEditData = (params: NewsCategoryType | null) => {
    this.categoriesEditData = params
  }

  setNewsFilter = (params: NewsParams) => {
    this.newsFilter = params
  }

  setTopNewsLoading = (params: boolean) => {
    this.topNewsLoading = params
  }

  setRecommendNewsLoading = (params: boolean) => {
    this.recommendNewsLoading = params
  }

  setNewsActiveTab = (params: Statuses) => {
    this.newsActiveTab = params
  }

  setNewsLoading = (params: boolean) => {
    this.newsLoading = params
  }

  setAllNews = (params: NewsResponse | null) => {
    this.allNews = params
  }

  setNewsCategoryModal = (params: boolean) => {
    this.newsCategoryModal = params
  }

  setOurTeamImagesLoading = (params: boolean) => {
    this.ourTeamImagesLoading = params
  }

  setOurTeamImages = (params: UploadFile[]) => {
    this.ourTeamImages = params
  }

  setFaqsFilter = (params: IPagination) => {
    this.faqsFilter = params
  }

  setFaqsLoading = (params: boolean) => {
    this.faqsLoading = params
  }

  setFaqs = (params: FaqsRes) => {
    this.faqs = params
  }

  setFaqEditData = (params: FaqType | null) => {
    this.faqEditData = params
  }

  setBranches = (params: Branch[]) => {
    this.branches = params
  }

  setVacancyTitle = (params: string | null) => {
    this.vacancyTitle = params
  }

  setCandidatesLoading = (params: boolean) => {
    this.candidatesLoading = params
  }

  setCandidates = (params: ICandidateRes | null) => {
    this.candidates = params
  }

  setCandidatesFilter = (params: ICandidateParams) => {
    this.candidatesFilter = params
  }

  setVacanciesEditData = (params: IVacancyType | null) => {
    this.vacanciesEditData = params
  }

  setVacanciesFilter = (params: IVacanciesParams) => {
    this.vacanciesFilter = params
  }

  setVacanciesLoading = (params: boolean) => {
    this.vacanciesLoading = params
  }

  setVacancies = (params: IVacanciesRes | null) => {
    this.vacancies = params
  }

  setIsPreviewNewsImg = (params: boolean) => {
    this.isPreviewNewsImg = params
  }

  setPreviewNewsImgUrl = (params: string | null) => {
    this.previewNewsImgUrl = params
  }

  setTopNew = (params: NewsType | null) => {
    this.topNew = params
  }

  setRecommendNew = (params: NewsType | null) => {
    this.recommendNew = params
  }

  setNewsModal = (params: NewsModal) => {
    this.newsModal = params
  }

  setFAQModal = (params: boolean) => {
    this.FAQModal = params
  }

  setIsPreviewImgVacancies = (params: boolean) => {
    this.isPreviewImgVacancies = params
  }

  setPreviewImgUrl = (params: string | null) => {
    this.previewImgUrl = params
  }

  setVacanciesModal = (params: boolean) => {
    this.vacanciesModal = params
  }

  setVacanciesActiveTab = (params: Statuses) => {
    this.vacanciesActiveTab = params
  }

  unTopNews = (id: number) => vacancyApi.unTopNews(id)

  toTopNews = (id: number) => vacancyApi.toTopNews(id)

  recommendNews = (id: number) => vacancyApi.recommendNews(id)

  unRecommendNews = (id: number) => vacancyApi.unRecommendNews(id)

  addToUnArchiveNews = (id: number) =>
    vacancyApi
      .addToUnArchiveNews(id)
      .then((res) => {
        if (res.success) {
          this.setNewsLoading(true)
          this.getAllNews()

          addSuccessNotification(vacancyDictionary.successFullyUnArchived)
        }
      })
      .catch(addCatchNotification)

  addToArchiveNews = (id: number) => vacancyApi.addToArchiveNews(id)

  getSingleNew = (id: number) =>
    vacancyApi
      .getSingleNews(id)
      .then((res) => {
        if (res.success) {
          this.setNewsSingle(res.data)
        }
      })
      .catch(addCatchNotification)

  addNew = (params: INewAddValues) =>
    vacancyApi.addNews({
      status: this.newsActiveTab,
      data: params,
    })

  editNew = (params: EditNewsType) => vacancyApi.editNews(params)

  getRecommendedNew = () =>
    vacancyApi
      .getRecommendNew()
      .then((res) => {
        if (res.success) {
          this.setRecommendNew(res.data)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setRecommendNewsLoading(false))

  getTopNew = () =>
    vacancyApi
      .getTopNew()
      .then((res) => {
        if (res.success) {
          this.setTopNew(res.data)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setTopNewsLoading(false))

  getAllNews = () =>
    vacancyApi
      .getAllNews({
        ...this.newsFilter,
        status: this.newsActiveTab,
      })
      .then((res) => {
        if (res.success) {
          this.setAllNews(res.data)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setNewsLoading(false))

  getOurTeamImages = () =>
    vacancyApi
      .getOurTeamImages()
      .then((res) => {
        if (res.success) {
          const images = res.data.teamBuildings.map((item) => ({
            thumbUrl: makeFileUrl(item.image)!,
            uid: item.id?.toString()!,
            name: 'Images',
          }))

          this.setOurTeamImages(images)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setOurTeamImagesLoading(false))

  addOurTeamImage = (params: OurTeamImage) => vacancyApi.addTeamImage(params)

  deleteOurTeamImage = (id: number) =>
    vacancyApi
      .deleteOurTeamImage(id)
      .then((res) => {
        if (res.success) {
          this.setOurTeamImagesLoading(true)
          this.getOurTeamImages()
        }
      })
      .catch(addCatchNotification)

  changePositionImage = (params: Position) =>
    vacancyApi
      .changePositionImage(params)
      .then((res) => {
        if (res.success) {
          this.setOurTeamImagesLoading(true)
          this.getOurTeamImages()
        }
      })
      .catch(addCatchNotification)

  getFaqs = () =>
    vacancyApi
      .getOurTeamFaqsAll(this.faqsFilter)
      .then((res) => {
        if (res.success) {
          this.setFaqs(res.data)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setFaqsLoading(false))

  addFaq = (params: FaqType) => vacancyApi.addOurTeamFaq(params)

  editFaq = (params: FaqEditType) => vacancyApi.editOurTeamFaq(params)

  deleteFaq = (id: number) =>
    vacancyApi
      .deleteOurTeamFaq(id)
      .then((res) => {
        if (res.success) {
          this.setFaqsLoading(true)
          this.getFaqs()
        }
      })
      .catch(addCatchNotification)

  changePositionFaq = (params: Position) =>
    vacancyApi
      .changePositionFaq(params)
      .then((res) => {
        if (res.success) {
          this.setFaqsLoading(true)
          this.getFaqs()
        }
      })
      .catch(addCatchNotification)

  getBranches = () =>
    vacancyApi
      .getBranches()
      .then((res) => {
        if (res.success) {
          this.setBranches(res.data)
        }
      })
      .catch(addCatchNotification)

  getCandidates = (params: ICandidateParams) =>
    vacancyApi
      .getVacancyCandidates(params)
      .then((res) => {
        if (res.success) {
          this.setCandidates(res.data)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setCandidatesLoading(false))

  getVacancies = () =>
    vacancyApi
      .getVacancies({
        ...this.vacanciesFilter,
        status: this.vacanciesActiveTab,
      })
      .then((res) => {
        if (res.success) {
          this.setVacancies(res.data)
        }
      })
      .catch(addCatchNotification)
      .finally(() => this.setVacanciesLoading(false))

  getSingleVacancy = (id: number) =>
    vacancyApi
      .getSingleVacancy(id)
      .then((res) => {
        if (res.success) {
          this.setVacancyTitle(res.data.title)
        }
      })
      .catch(addCatchNotification)

  addVacancies = (params: IVacancyType) => vacancyApi.addVacancies(params)

  editVacancies = (params: EditVacancyType) => vacancyApi.editVacancies(params)

  changeVacancyPosition = (params: VacancyPositionChange) =>
    vacancyApi
      .changePositionVacancy(params)
      .then((res) => {
        if (res.success) {
          this.setVacanciesLoading(true)
          this.getVacancies()
        }
      })
      .catch(addCatchNotification)

  addVacanciesArchive = (id: number) =>
    vacancyApi
      .addVacanciesArchive(id)
      .then((res) => {
        if (res.success) {
          this.setVacanciesLoading(true)
          this.getVacancies()
        }
      })
      .catch(addCatchNotification)

  addVacanciesUnArchive = (id: number) =>
    vacancyApi
      .addVacanciesUnArchive(id)
      .then((res) => {
        if (res.success) {
          this.setVacanciesLoading(true)
          this.getVacancies()
        }
      })
      .catch(addCatchNotification)

  reset = () => {
    this.vacanciesModal = false
    this.vacanciesActiveTab = Statuses.Active
    this.isPreviewImgVacancies = false
    this.previewImgUrl = null
    this.FAQModal = false
    this.newsModal = null
    this.topNew = null
    this.recommendNew = null
    this.isPreviewNewsImg = false
    this.previewNewsImgUrl = null
    this.vacancies = null
    this.vacanciesLoading = false
    this.vacanciesFilter = { page: 1, perPage: 10 }
    this.vacanciesEditData = null
    this.vacancyTitle = null
    this.candidates = null
    this.candidatesLoading = false
    this.branches = []
    this.candidatesFilter = { page: 1, perPage: 10 }
    this.faqs = null
    this.faqEditData = null
    this.faqsLoading = false
    this.faqsFilter = { page: 1, perPage: 10 }
    this.ourTeamImages = []
    this.ourTeamImagesLoading = false
    this.newsCategoryModal = false
    this.allNews = null
    this.newsSingle = null
    this.newsLoading = false
    this.recommendNewsLoading = false
    this.topNewsLoading = false
    this.newsActiveTab = Statuses.Active
    this.newsFilter = { page: 1, perPage: 10 }
  }
}

export const vacancyStore = new VacancyStore()
